<template>
  <div :style="{width:'100%', maxWidth: maxWidth}">
    <svg :viewBox="`0 0 ${scaledSize} ${scaledSize}`" :width="'100%'" height="auto" role="img">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="2" dy="2" stdDeviation="3" flood-opacity="0.3"/>
        </filter>
      </defs>

      <!-- CAPABILITY CIRCLES -->
      <g v-for="(circle, i) in circleLayout" :key="i">
        <circle
          :cx="circle.x"
          :cy="circle.y"
          :r="circle.radius"
          :fill="circle.color"
          :opacity="selectedItems.includes(circle.originalIndex) ? 0.95 : 0.8"
          :stroke="selectedItems.includes(circle.originalIndex) ? '#fff' : 'none'"
          :stroke-width="selectedItems.includes(circle.originalIndex) ? 3 : 0"
          style="cursor:pointer; transition: all 0.3s ease"
          filter="url(#shadow)"
          @click="onPick(i, circle)"
          @mouseenter="hoveredIndex = i"
          @mouseleave="hoveredIndex = null"
        />
        <!-- Selection ring -->
        <circle
          v-if="selectedItems.includes(circle.originalIndex)"
          :cx="circle.x"
          :cy="circle.y"
          :r="circle.radius + 8"
          fill="none"
          :stroke="circle.color"
          stroke-width="2"
          opacity="0.6"
          style="transition: all 0.3s ease"
        />
        <!-- Code -->
        <text
          :x="circle.x"
          :y="circle.y - 15"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.codeFontSize"
          font-weight="800"
          style="pointer-events:none"
        >{{ circle.code }}</text>
        <!-- First label line -->
        <text
          :x="circle.x"
          :y="circle.y + 2"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.labelFontSize"
          font-weight="600"
          style="pointer-events:none"
        >{{ circle.firstLine }}</text>
        <!-- Second label line -->
        <text
          v-if="circle.secondLine"
          :x="circle.x"
          :y="circle.y + 15"
          text-anchor="middle"
          dominant-baseline="middle"
          fill="white"
          :font-size="circle.labelFontSize"
          font-weight="600"
          style="pointer-events:none"
        >{{ circle.secondLine }}</text>
      </g>

      <!-- RADIAL BANDS FOR DOMAINS -->
      <path :d="getRadialBandPath('knowing')" fill="#7A3C9A" fill-opacity="0.5" stroke="none"/>
      <path :d="getRadialBandPath('being')"   fill="#3DB09E" fill-opacity="0.5" stroke="none"/>
      <path :d="getRadialBandPath('doing')"   fill="#3F95C8" fill-opacity="0.7" stroke="none"/>

      <!-- Curved text paths -->
      <defs>
        <path :id="`knowing-path-${_uid}`" :d="getCurvedTextPath('knowing')" />
        <path :id="`being-path-${_uid}`"   :d="getCurvedTextPath('being')" />
        <path :id="`doing-path-${_uid}`"   :d="getCurvedTextPath('doing')" />
      </defs>

      <!-- Curved band labels -->
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800"
            style="pointer-events:none" text-anchor="middle">
        <textPath :href="`#knowing-path-${_uid}`" startOffset="50%">Knowing</textPath>
      </text>
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800"
            style="pointer-events:none" text-anchor="middle">
        <textPath :href="`#being-path-${_uid}`" startOffset="50%">Being</textPath>
      </text>
      <text :font-size="scaledHubTitleSize * 0.8" fill="white" font-weight="800"
            style="pointer-events:none" text-anchor="middle">
        <textPath :href="`#doing-path-${_uid}`" startOffset="57%" side="right">Doing</textPath>
      </text>

      <!-- CENTER HUB -->
      <circle :cx="centerX" :cy="centerY" :r="scaledHubRadius" fill="#fff" stroke="#ddd"
              :stroke-width="scaledStrokeWidth" filter="url(#shadow)"/>
      <text :x="centerX" :y="centerY - (15 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubTitleSize" fill="#04748e" font-weight="800" style="pointer-events:none">
        ELDAC
      </text>
      <text :x="centerX" :y="centerY" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubTitleSize" fill="#04748e" font-weight="800" style="pointer-events:none">
        Capabilities
      </text>
      <text :x="centerX" :y="centerY + (20 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubSubSize" fill="#0D7693" font-weight="600" style="pointer-events:none">
        End of Life
      </text>
      <text :x="centerX" :y="centerY + (32 * scale)" text-anchor="middle" dominant-baseline="middle"
            :font-size="scaledHubSubSize" fill="#0D7693" font-weight="600" style="pointer-events:none">
        Directions for Aged Care
      </text>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'CapabilityDonut',
  props: {
    size:         { type: Number, default: 500 },
    scale:        { type: Number, default: 1 },
    outerRadius:  { type: Number, default: 460 },
    innerRadius:  { type: Number, default: 230 },
    hubRadius:    { type: Number, default: 100 },
    groupBand:    { type: Number, default: 80 },
    gapDegrees:   { type: Number, default: 3 },
    startAngle:   { type: Number, default: -90 },
    gapColor:     { type: String, default: '#ffffff' },
    gapPx:        { type: Number, default: 6 },
    maxWidth:     { type: String, default: '100%' },
    hubTitle:     { type: String, default: 'ELDAC Capabilities' },
    hubSub:       { type: String, default: 'End of Life Directions for Aged Care' },
    labelFontPx:  { type: Number, default: 11 },
    groupFontPx:  { type: Number, default: 7 },
    hubTitlePx:   { type: Number, default: 16 },
    hubSubPx:     { type: Number, default: 12 },
    groups: {
      type: Array,
      default: () => ([
        { name: 'Knowing', color: '#7A3C9A', textColor: '#fff', flip: false },
        { name: 'Being',   color: '#3DB09E', textColor: '#fff', flip: false },
        { name: 'Doing',   color: '#3F95C8', textColor: '#fff', flip: true },
      ])
    },
    items: {
      type: Array,
      default: () => ([
        { label: 'Principles & Values',      code: 'K1', group: 0, index: 0 },
        { label: 'Unique Responses',         code: 'K2', group: 0, index: 1 },
        { label: 'Communication',            code: 'D1', group: 2, index: 2 },
        { label: 'Inclusive Care',           code: 'D2', group: 2, index: 3 },
        { label: 'Collaborative Care',       code: 'D3', group: 2, index: 4 },
        { label: 'Legislation & Frameworks', code: 'B1', group: 1, index: 5 },
        { label: 'Ongoing Development',      code: 'B2', group: 1, index: 6 },
      ])
    },
    value: { type: Array, default: () => [] },
    groupOpacity: { type: Number, default: 0.25 }
  },

  data() {
    return {
      selectedItems: Array.isArray(this.value) ? [...this.value] : (this.value !== -1 ? [this.value] : []),
      hoveredIndex: null
    };
  },

  watch: {
    value(v) {
      this.selectedItems = Array.isArray(v) ? [...v] : (v !== -1 ? [v] : []);
    }
  },

  computed: {
    scaledSize()          { return this.size * this.scale; },
    scaledHubRadius()     { return this.hubRadius * this.scale; },
    scaledStrokeWidth()   { return 2 * this.scale; },
    scaledHubTitleSize()  { return this.hubTitlePx * this.scale; },
    scaledHubSubSize()    { return this.hubSubPx * this.scale; },
    scaledLabelFontSize() { return this.labelFontPx * this.scale; },
    scaledCodeFontSize()  { return 16 * this.scale; },
    centerX() { return this.scaledSize / 2; },
    centerY() { return this.scaledSize / 2; },

    sliceColors() {
      const map = {};
      this.groups.forEach((g, gi) => { map[gi] = g.color; });
      return map;
    },

    circleLayout() {
      const circles = [];
      const center = { x: this.centerX, y: this.centerY };
      const radius = 80 * this.scale;
      const centerOffset = 160 * this.scale;
      const n = 7;
      const angleStep = (2 * Math.PI) / n;
      // Start from bottom (90° = 6 o'clock) for D2
      const startAngle = Math.PI / 2;

      const positions = [];
      for (let i = 0; i < n; i++) {
        const angle = startAngle + (i * angleStep);
        positions.push({
          x: center.x + centerOffset * Math.cos(angle),
          y: center.y + centerOffset * Math.sin(angle),
          radius
        });
      }

      // Arrange items so each domain's bubbles are grouped together.
      // Position 0 = bottom (6 o'clock), clockwise.
      const capabilityOrder = [
        'Inclusive Care',           // D2 - bottom
        'Communication',            // D1 - lower left
        'Unique Responses',         // K2 - upper left
        'Principles & Values',      // K1 - top
        'Legislation & Frameworks', // B1 - upper right
        'Ongoing Development',      // B2 - right
        'Collaborative Care',       // D3 - lower right
      ];

      capabilityOrder.forEach((capabilityName, index) => {
        const item = this.items.find(item => item.label === capabilityName);
        if (item && positions[index]) {
          const pos = positions[index];
          const groupColor = this.sliceColors[item.group] || '#04748e';
          const textLines = this.splitLabelText(item.label);
          circles.push({
            x: pos.x,
            y: pos.y,
            radius: pos.radius,
            color: groupColor,
            code: item.code || this.getCapabilityCode(item.label),
            firstLine: textLines.first,
            secondLine: textLines.second,
            fullLabel: item.label,
            codeFontSize: this.scaledCodeFontSize,
            labelFontSize: this.scaledLabelFontSize,
            group: item.group,
            originalIndex: item.index
          });
        }
      });

      return circles;
    }
  },

  methods: {
    getCapabilityCode(label) {
      const codeMap = {
        'Principles & Values':      'K1',
        'Unique Responses':         'K2',
        'Communication':            'D1',
        'Inclusive Care':           'D2',
        'Collaborative Care':       'D3',
        'Legislation & Frameworks': 'B1',
        'Ongoing Development':      'B2'
      };
      return codeMap[label] || 'C';
    },

    splitLabelText(label) {
      const splitMap = {
        'Principles & Values':      { first: 'Principles', second: '& Values' },
        'Unique Responses':         { first: 'Unique', second: 'Responses' },
        'Communication':            { first: 'Communication', second: null },
        'Inclusive Care':           { first: 'Inclusive', second: 'Care' },
        'Collaborative Care':       { first: 'Collaborative', second: 'Care' },
        'Legislation & Frameworks': { first: 'Legislation', second: '& Frameworks' },
        'Ongoing Development':      { first: 'Ongoing', second: 'Development' }
      };
      return splitMap[label] || { first: label, second: null };
    },

    onPick(i, circle) {
      const originalIndex = circle.originalIndex;
      if (this.selectedItems.includes(originalIndex)) {
        this.selectedItems = this.selectedItems.filter(idx => idx !== originalIndex);
      } else {
        this.selectedItems = [...this.selectedItems, originalIndex];
      }
      this.$emit('input', this.selectedItems);
      this.$emit('select', {
        selectedIndices: this.selectedItems,
        toggledIndex: originalIndex,
        toggledItem: this.items[originalIndex],
        circle,
        isSelected: this.selectedItems.includes(originalIndex)
      });
    },

    getRadialBandPath(domain) {
      const center = { x: this.centerX, y: this.centerY };
      const innerRadius = this.scaledHubRadius + (5 * this.scale);
      const outerRadius = this.scaledHubRadius + (25 * this.scale);
      const angleStep = 360 / 7;
      const startAngle = 90; // bottom = position 0

      const angleRanges = {
        // Doing: positions 0, 1, 6
        doing: {
          start: startAngle - (angleStep * 1.5),
          span:  angleStep * 3
        },
        // Knowing: positions 2, 3
        knowing: {
          start: startAngle + (angleStep * 1.5),
          span:  angleStep * 2
        },
        // Being: positions 4, 5
        being: {
          start: startAngle + (angleStep * 3.5),
          span:  angleStep * 2
        }
      };

      const range = angleRanges[domain];
      if (!range) return '';

      const startRad = (range.start * Math.PI) / 180;
      const endRad   = ((range.start + range.span) * Math.PI) / 180;

      const x1 = center.x + innerRadius * Math.cos(startRad);
      const y1 = center.y + innerRadius * Math.sin(startRad);
      const x2 = center.x + outerRadius * Math.cos(startRad);
      const y2 = center.y + outerRadius * Math.sin(startRad);
      const x3 = center.x + outerRadius * Math.cos(endRad);
      const y3 = center.y + outerRadius * Math.sin(endRad);
      const x4 = center.x + innerRadius * Math.cos(endRad);
      const y4 = center.y + innerRadius * Math.sin(endRad);

      const largeArc = range.span > 180 ? 1 : 0;

      return `M ${x1} ${y1} L ${x2} ${y2} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x1} ${y1} Z`;
    },

    getCurvedTextPath(domain) {
      const center = { x: this.centerX, y: this.centerY };
      const group = this.groups.find(g => g.name.toLowerCase() === domain);
      const shouldFlip = group ? group.flip : false;
      const radius = this.scaledHubRadius + (shouldFlip ? 15 : 10) * this.scale;

      const angleStep = 360 / 7;
      const startAngle = 90;

      const curveSpans = {
        doing:   { start: startAngle - (angleStep * 1.5), span: angleStep * 3.5 },
        knowing: { start: startAngle + (angleStep * 1.5), span: angleStep * 2 },
        being:   { start: startAngle + (angleStep * 3.5), span: angleStep * 2 }
      };

      const curve = curveSpans[domain];
      if (!curve) return '';

      let startRad, endRad;
      if (shouldFlip) {
        startRad = ((curve.start + curve.span) * Math.PI) / 180;
        endRad   = (curve.start * Math.PI) / 180;
      } else {
        startRad = (curve.start * Math.PI) / 180;
        endRad   = ((curve.start + curve.span) * Math.PI) / 180;
      }

      const x1 = center.x + radius * Math.cos(startRad);
      const y1 = center.y + radius * Math.sin(startRad);
      const x2 = center.x + radius * Math.cos(endRad);
      const y2 = center.y + radius * Math.sin(endRad);

      const largeArc  = curve.span > 180 ? 1 : 0;
      const sweepFlag = shouldFlip ? 0 : 1;

      return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} ${sweepFlag} ${x2} ${y2}`;
    }
  }
};
</script>
